/** @file islandsOf: the separate figures of a sheet (parts spread apart) as labelled boxes with pixel counts. */
import type { Image } from '../types/image';
import { dilateMask } from './dilateMask';
import { flatBackgroundOf } from './flatBackgroundOf';
import { foregroundMask } from './foregroundMask';
import type { Island, IslandMap, IslandOptions } from './islandTypes';
import { labelComponents } from './labelComponents';

/** Boxes and counts per label 1..count over the counted pixels of `labels`. */
function boxesOf(labels: Int32Array, width: number, count: number): Island[] {
  const boxes: Island[] = Array.from({ length: count }, (_, i) => ({ id: i + 1, x: Infinity, y: Infinity, w: -Infinity, h: -Infinity, pixels: 0 }));
  for (let p = 0; p < labels.length; p++) {
    const b = boxes[(labels[p] ?? 0) - 1];
    if (b === undefined) continue;
    const x = p % width;
    const y = (p - x) / width;
    b.x = Math.min(b.x, x);
    b.y = Math.min(b.y, y);
    b.w = Math.max(b.w, x);
    b.h = Math.max(b.h, y);
    b.pixels += 1;
  }
  return boxes.filter((b) => b.pixels > 0).map((b) => ({ ...b, w: b.w - b.x + 1, h: b.h - b.y + 1 }));
}

/**
 * Connected regions of counted pixels (alpha above `alphaThreshold`, not the flat background the four corners agree
 * on). With `merge` > 0, regions at most `merge` px apart (rounded up to even) become one island: the components of the mask grown by
 * `ceil(merge / 2)` px give the grouping, the counts and boxes come from the ungrown pixels. Islands under `minPixels`
 * are dropped and their labels cleared; ids are renumbered 1..n in scan order (top-most first).
 */
export function islandsOf(image: Image, options: IslandOptions = {}): IslandMap {
  const tolerance = options.tolerance ?? 32;
  const background = flatBackgroundOf(image, tolerance);
  const mask = foregroundMask(image, options.alphaThreshold ?? 0, background, tolerance);
  const grown = dilateMask(mask, image.width, image.height, Math.ceil((options.merge ?? 0) / 2));
  const { labels, count } = labelComponents(grown, image.width);
  for (let p = 0; p < mask.length; p++) if (mask[p] === 0) labels[p] = 0;
  const kept = boxesOf(labels, image.width, count).filter((b) => b.pixels >= (options.minPixels ?? 1));
  const renumber = new Int32Array(count + 1);
  kept.forEach((b, i) => { renumber[b.id] = i + 1; });
  for (let p = 0; p < labels.length; p++) labels[p] = renumber[labels[p] ?? 0] ?? 0;
  const islands = kept.map((b, i) => ({ ...b, id: i + 1 }));
  return { width: image.width, height: image.height, labels, islands, background };
}
